import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  useTheme,
  Alert,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import { tokens } from "../../theme";
import useAuth from "../../hooks/auth_hook";
import usePayment from "../../hooks/payment_hook";
import { useNavigate, useLocation } from "react-router-dom";
import "./checkout.css";
import faceLaughBeam from "../images/fa-icons/face-laugh-beam.svg";
import PricingOption from "../../components/checkout/PricingOption";

// This component handles redirecting to Lemon Squeezy checkout
export default function Checkout() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { isAuthenticated, isLoading, user } = useAuth();
  // Always check payment status in the checkout component
  const { hasPaid, isCheckingPayment, isPaymentLoading, getCheckoutUrl, checkout_url } = usePayment();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("yearly");
  const navigate = useNavigate();
  const location = useLocation();

  // Handler for billing period toggle
  const handleBillingPeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newBillingPeriod: "monthly" | "yearly"
  ) => {
    if (newBillingPeriod !== null) {
      setBillingPeriod(newBillingPeriod);
    }
  };

  const redirectToCheckout = (productId: string, billingPeriod: "monthly" | "yearly") => {
    setIsRedirecting(true);

    // Map product IDs to their respective Lemon Squeezy checkout URLs
    const checkoutParams: Record<string, Record<"monthly" | "yearly", string>> = {
      // Less than 10k Subscribers plan
      "under_10k_subs": {
        monthly: "under_10k_subs_monthly", // Replace with actual URL
        yearly: "under_10k_subs_yearly"    // Replace with actual URL
      },
      // Everything plan
      "the_everything_plan": {
        monthly: "the_everything_plan_monthly", // Replace with actual URL
        yearly: "the_everything_plan_yearly"    // Replace with actual URL
      }
    };

    // Get the appropriate checkout URL based on the selected product and billing period
    if (!checkoutParams[productId]) {
      console.error(`Error: No checkout URLs defined for product ID: ${productId}`);
      alert("Configuration error: Invalid product selected. Please contact support.");
      setIsRedirecting(false);
      return;
    }

    if (!checkoutParams[productId][billingPeriod]) {
      console.error(`Error: No checkout URL defined for product ID: ${productId} with billing period: ${billingPeriod}`);
      alert("Configuration error: Invalid billing period selected. Please contact support.");
      setIsRedirecting(false);
      return;
    }

    const variant = checkoutParams[productId][billingPeriod];

    // Log checkout information for debugging
    console.log(`Checkout initiated for product: ${productId}, billing period: ${billingPeriod}`);
    console.log(`Using variant: ${variant}`);

    // Call getCheckoutUrl with the variant ID
    getCheckoutUrl(variant);
  };

  // Redirect to app if user has already paid
  useEffect(() => {
    if (isAuthenticated && user && !isLoading && !isCheckingPayment && hasPaid) {
      // User is authenticated and has paid, redirect to app
      navigate("/add");
    }
  }, [isAuthenticated, isLoading, user, hasPaid, isCheckingPayment, navigate]);

  // Redirect to checkout URL when it's available
  useEffect(() => {
    if (checkout_url && isRedirecting) {
      console.log(`Redirecting to checkout URL: ${checkout_url}`);
      window.location.href = checkout_url;
    }
  }, [checkout_url, isRedirecting]);

  if (isLoading || isCheckingPayment) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (showSuccess) {
    return (
      <Box
        sx={{
          width: "100%",
          overflowY: "auto",
          paddingX: 6,
          paddingTop: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}
      >
        <Alert
          severity="success"
          sx={{
            width: "100%",
            maxWidth: 600,
            mb: 4,
            fontSize: "1.2rem",
            "& .MuiAlert-icon": {
              fontSize: "2rem"
            }
          }}
        >
          Payment successful! You now have full access to the application. Redirecting you to the app...
        </Alert>
      </Box>
    );
  }

  // Show checkout for users who haven't paid
  return (
    <section id="pricing" className="pt-[120px] pb-20 mx-auto">
      <div className="container">
        <div className="flex flex-wrap mx-[0px] justify-center">
          <div className="w-full px-4">
            <div className="mx-auto max-w-[655px] text-center mb-20 wow fadeInUp" data-wow-delay=".1s">
              <span className="text-lg font-semibold text-primary mb-2 block"> Best Service, Best Price. </span>
              <h2 className="text-black font-bold text-3xl sm:text-4xl md:text-[45px] mb-5">Our Pricing Plan</h2>
              <p
                className="text-body-color text-base md:text-lg leading-relaxed md:leading-relaxed max-w-[570px] mx-auto">
                Both plans have the same features. 50% discount for new creators with less than 10k
                subscribers. You can keep the discount for life.
              </p>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 4 }}>
                <ToggleButtonGroup
                  value={billingPeriod}
                  exclusive
                  onChange={handleBillingPeriodChange}
                  aria-label="billing period"
                  color="primary"
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    "& .MuiToggleButton-root": {
                      px: 3,
                      py: 1,
                      border: `1px solid ${colors.primary[500]}`,
                      "&.Mui-selected": {
                        backgroundColor: colors.primary[500],
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: colors.primary[600]
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="monthly" aria-label="monthly billing">
                    Monthly
                  </ToggleButton>
                  <ToggleButton value="yearly" aria-label="yearly billing">
                    Yearly
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-center mx-[-16px]">
          <PricingOption
            title="Less than 10k Subs"
            price={billingPeriod === "yearly" ? "$18" : "$24"}
            features={[
              "Unlimited Thumbnail A/B Tests",
              "Unlimited Title A/B Tests",
              "Unlimited Thumbnail's with AI Generated Text",
              "Unlimited AI Generated Titles",
              "Hourly, Daily, Weekly, and Custom Length Tests",
              "1 Channel"
            ]}
            onPurchase={redirectToCheckout}
            billingPeriod={billingPeriod}
            productId="under_10k_subs"
            isPaymentLoading={isPaymentLoading}
          />
          <PricingOption
            title="The Everything Plan"
            price={billingPeriod === "yearly" ? "$37" : "$49"}
            features={[
              "Unlimited Thumbnail A/B Tests",
              "Unlimited Title A/B Tests",
              "Unlimited Thumbnail's with AI Generated Text",
              "Unlimited AI Generated Titles",
              "Hourly, Daily, Weekly, and Custom Length Tests",
              "1 Channel"
            ]}
            onPurchase={redirectToCheckout}
            billingPeriod={billingPeriod}
            productId="the_everything_plan"
            isPaymentLoading={isPaymentLoading}
          />
          <div className="shadow-pricing p-4 rounded-2xl"><p>P.S. When we say unlimited, we mean it. <br />Want to
            test
            1000 thumbnails
            and 1000
            titles? Go for it, (it
            might take a
            few months, but that's up to you!)</p></div>
        </div>
      </div>
    </section>

  );
}
